import React from 'react';
import * as Yup from 'yup';
import AllForm from './Form';
import {
  Box,
  Typography,
  Container,
  Paper,
  Link,
  Checkbox,
  FormControlLabel,
  Button,
  useMediaQuery,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { theme } from '../../theme/theme';
import { COLORS } from '../../theme/themeConstants';
import { CreateLogin } from '../Query/LoginQuery';
import { useLoginApi } from '../../hooks/useLoginApi';
import AuthLayout from './AuthLayout';
import { updateUserData } from '../../context/PermissionContext';
import { getInitialPage } from '../HelperFunctions/atoms';

const validationSchema = Yup.object().shape({
  email: Yup.string()
    .matches(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9-]+\.[a-zA-Z]{2,3}$/,'Please enter a valid email address')
    .nullable(),
  phone: Yup.string()
    .matches(/^\+[1-9]{1,3}\d{7,10}$/, 'Please enter a valid phone number')
    .nullable(),
  password: Yup.string()
    .required('Please enter the password')
    .min(8, 'Password must be at least 8 characters'),
}).test(
    'email-or-phone',
    'Email or phone is required',
    function (values) {
      return !!(values.email || values.phone);
    }
  );

const Login: React.FC = () => {
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isAbove1000 = useMediaQuery('(min-width:1001px)');
  const [authData, setAuthData] = React.useState<any>(null);
  const [loginMethod, setLoginMethod] = React.useState<'email' | 'phone'>('email');
  const { mutate: LoginCreate, isPending: LoginCreateLoading } = CreateLogin();
  const { getSpecialUser } = useLoginApi();

  const navigate = useNavigate();
  const [rememberMe, setRememberMe] = React.useState(false);

  const handleLogin = async (values: Record<string, any>) => {
    try {
      const emailOrPhone = loginMethod === 'email' ? { email: values.email } : { phoneNumber: values.phone }
      const modifiedValues = {
        ...emailOrPhone,
        password: values.password,
        channel: loginMethod,
        platformType: 'admin',
      };
      LoginCreate(modifiedValues, {
        onSuccess: async (response: any) => {
          const { token, userId, userType } = response || {};

          if (!token) throw new Error('No token received');

          const newAuthData = {
            token,
            userId,
            userType,
            isAuthenticated: true,
            email: values.email,
            lastLogin: new Date().toISOString(),
          };

          setAuthData(newAuthData);

          updateUserData(newAuthData);
        },
        onError: (error: any) => {
          console.log(error);
        },
      });
    } catch (error) {
      console.error('Login error:', error);
      // Handle error, e.g., show a snackbar or alert
    }
  };

  // When switching login method:
  const handleLoginMethodChange = (method: 'email' | 'phone') => {
    setLoginMethod(method);
    // setValue('password', ''); // Clear password value
    // clearErrors('password');  // Clear password error
    if (method === 'email') {
      // setValue('phone', '');
      // setValue('countryCode', '+962'); // or your default
      // clearErrors('phone');
    } else {
      // setValue('email', '');
      // clearErrors('email');
    }
  };

  React.useEffect(() => {
    const userData = localStorage.getItem('userData');
    if (userData) {
      const parsedData = JSON.parse(userData);
      if (parsedData?.userId) {
        const permissions = parsedData.userType === 'subAdmin' && parsedData.userDetails && 'permissions' in parsedData.userDetails ? parsedData.userDetails.permissions : null;
        const initialPage = getInitialPage(parsedData.userType, permissions);
        navigate(initialPage);
      } else {
        navigate('/');
      }
    }
  }, []);

  React.useEffect(() => {
    if (!authData) return;
    if (!authData.userId || !authData.userType) {
      console.error('userId or userType missing in authData', authData);
      return;
    }

    getSpecialUser({ userId: authData.userId, userType: authData.userType })
      .then((data) => {
        if (!data) {
          console.error('No user details found for userId:', authData.userId);
          return;
        }
        const updatedUserData = { ...authData, userDetails: data };
        updateUserData(updatedUserData);

        const permissions = authData.userType === 'subAdmin' && data && 'permissions' in data ? data.permissions : null;
        const initialPage = getInitialPage(authData.userType, permissions);
        navigate(initialPage);
      })
      .catch((err) => {
        console.error('Failed to fetch user details', err);
      });
  }, [authData]);

  const LoginForm = AllForm({
    initialValues: {
      email: '',
      phone: '',
      password: '',
    },
    validationSchema,
    onSubmit: handleLogin,
    fields: loginMethod === 'email' ? [
      {
        name: 'email',
        label: 'Email',
        type: 'text',
        // dependsOn: {field: loginMethod, value: 'email'},
        isRequired: true,
      },
      {
        name: 'password',
        label: 'Password',
        type: 'password',
        isRequired: true,
      },
      {
        name: 'rememberMe',
        type: 'custom',
        component: () => (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mt: 1,
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
              }
              label={<Typography variant="body2">Remember me</Typography>}
            />
            <Link
              onClick={(e) => {
                e.preventDefault();
                navigate(`/forgot-password?channel=${encodeURIComponent(loginMethod)}`);
              }}
              sx={{
                color: '#ff6a3d',
                fontWeight: 500,
                fontSize: 14,
                fontFamily: 'Livvic',
                cursor: 'pointer',
              }}
            >
              Forgot password?
            </Link>
          </Box>
        ),
      },
    ] : [
      {
        name: 'phone',
        label: 'Mobile Number',
        type: 'text',
        // dependsOn: {field: loginMethod, value: 'phone'},
        isRequired: true,
      },
      {
        name: 'password',
        label: 'Password',
        type: 'password',
        isRequired: true,
      },
      {
        name: 'rememberMe',
        type: 'custom',
        component: () => (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mt: 1,
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
              }
              label={<Typography variant="body2">Remember me</Typography>}
            />
            <Link
              onClick={(e) => {
                e.preventDefault();
                navigate(`/forgot-password?channel=${encodeURIComponent(loginMethod)}`);
              }}
              sx={{
                color: '#ff6a3d',
                fontWeight: 500,
                fontSize: 14,
                fontFamily: 'Livvic',
                cursor: 'pointer',
              }}
            >
              Forgot password?
            </Link>
          </Box>
        ),
      },
    ],
    customButton: (formikProps: any) => (
      // <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
      //   <button
      //     type="submit"
      //     style={{
      //       width: '40%',
      //       backgroundColor: 'rgb(76, 10, 39)',
      //       color: '#fff',
      //       border: 'none',
      //       borderRadius: 6,
      //       fontSize: 16,
      //       fontFamily: 'Livvic',
      //       fontWeight: 600,
      //       marginLeft: 20,
      //       cursor: 'pointer',
      //       transition: 'background 0.3s',
      //       padding: '15px 50px',
      //     }}
      //     disabled={formikProps.isSubmitting}
      //   >
      //     {formikProps.isSubmitting ? 'Logging in...' : 'Log in to Admin Panel'}
      //   </button>
      // </div>
      <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
        <button
          type="submit"
          style={{
            backgroundColor: COLORS.primary.main,
            color: COLORS.secondary.main,
            border: 'none',
            borderRadius: 6,
            fontSize: 16,
            fontFamily: 'Livvic',
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'background 0.3s',
            padding: '10px 20px', // Reduced padding
            whiteSpace: 'nowrap', // Prevents wrapping
          }}
          disabled={formikProps.isSubmitting}
        >
          {LoginCreateLoading ? 'Logging in...' : 'Login'}
        </button>
      </div>
    ),
  });

  return (
    <ThemeProvider theme={theme}>
      <AuthLayout>
        <Container maxWidth="md">
          <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
            <Typography
              component="h1"
              variant="h5"
              sx={{
                mb: 2,
                textAlign: 'center',
                color: COLORS.secondary.main,
                fontWeight: 'bold',
              }}
            >
              Foodigo Admin Login
            </Typography>
            <Typography
              variant="body2"
              sx={{ textAlign: 'center', mb: 3, color: '#888' }}
            >
              Enter your credentials to access the dashboard
            </Typography>

            {/* Login Method Toggle */}
            <Box sx={{
              // mb: isMobile ? 1 : 2,
              mt: isMobile ? 2 : 3,
              display: 'flex',
              justifyContent: isMobile ? 'center' : 'center',
              gap: 1,
            }}>
              <Button
                variant={loginMethod === 'email' ? 'contained' : 'outlined'}
                onClick={() => handleLoginMethodChange('email')}
                size={isMobile ? 'small' : isAbove1000 ? 'small' : 'medium'}
                sx={{
                  bgcolor: loginMethod === 'email' ? COLORS.primary.main : 'transparent',
                  color: COLORS.secondary.main,
                  borderColor: COLORS.secondary.main,
                  fontSize: isMobile ? '0.875rem' : isAbove1000 ? '0.95rem' : '1rem',
                  px: isMobile ? 2 : 3,
                  py: isMobile ? 1 : 1.5,
                  '&:hover': {
                    bgcolor: loginMethod === 'email' ? COLORS.primary.dark : 'rgba(100, 0, 49, 0.1)',
                    borderColor: COLORS.secondary.main,
                  },
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              >
                Email
              </Button>
              <Button
                variant={loginMethod === 'phone' ? 'contained' : 'outlined'}
                onClick={() => handleLoginMethodChange('phone')}
                size={isMobile ? 'small' : isAbove1000 ? 'small' : 'medium'}
                sx={{
                  bgcolor: loginMethod === 'phone' ? COLORS.primary.main : 'transparent',
                  color: COLORS.secondary.main,
                  borderColor: COLORS.secondary.main,
                  fontSize: isMobile ? '0.875rem' : isAbove1000 ? '0.95rem' : '1rem',
                  px: isMobile ? 2 : 3,
                  py: isMobile ? 1 : 1.5,
                  '&:hover': {
                    bgcolor: loginMethod === 'phone' ? COLORS.primary.dark : 'rgba(100, 0, 49, 0.1)',
                    borderColor: COLORS.secondary.main,
                  },
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              >
                Phone
              </Button>
            </Box>
            {LoginForm()}
            <Box
              sx={{
                mt: 3,
                textAlign: 'center',
                fontSize: 13,
                color: '#888',
                fontFamily: 'Livvic',
              }}
            >
              By logging in, you agree to our{' '}
              <Link
                href="#"
                sx={{ color: '#8f5aff', fontWeight: 500, fontFamily: 'Livvic' }}
              >
                Terms of Service
              </Link>{' '}
              and{' '}
              <Link
                href="#"
                sx={{ color: '#ff6a3d', fontWeight: 500, fontFamily: 'Livvic' }}
              >
                Privacy Policy
              </Link>
            </Box>
          </Paper>
        </Container>
      </AuthLayout>
    </ThemeProvider>
  );
};

export default Login;
